import React from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { ActivityEvent } from '@/types';
import type { PositionDetail } from '@/types/trades.types';
import {
  FAMILY_META,
  resolveEventLabel,
  resolveLegLabel,
  resolveActor,
  resolveDotColor,
  formatSignedImpact,
} from '@/features/trades/utils/activity-family';
import { formatDate, formatTimeUtc } from '@/utils/format';

interface ActivityRowProps {
  event: ActivityEvent;
  /** Stable row id (occurred_at + index) — used for aria-controls + the expand region. */
  rowId: string;
  isExpanded: boolean;
  onToggle: () => void;
  trade: PositionDetail;
}

/** Event types whose title renders in the terminal-adverse red. (No 'cancelled':
 *  the backend maps a cancelled position to event_type 'terminated' — LR-436 D3.) */
const RED_TITLE_TYPES = new Set(['terminated']);

/** Initials for the actor avatar (aria-hidden — the name is the real label). */
function initialsOf(label: string): string {
  const parts = label.replace(/[^a-zA-Z0-9@ ]/g, ' ').trim().split(/[\s@]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Render the actor as an avatar + name — user avatar accent, others neutral. */
function ActorPill({ event }: { event: ActivityEvent }) {
  const label = resolveActor(event);
  const isUser = event.actor.kind === 'user';
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11.5px] text-lr-muted">
      <span
        aria-hidden="true"
        className={clsx(
          'grid h-[17px] w-[17px] place-items-center rounded-full text-[9px] font-bold',
          isUser ? 'bg-lr-accent-weak text-lr-accent' : 'bg-gray-200 text-gray-600',
        )}
      >
        {initialsOf(label)}
      </span>
      {label}
    </span>
  );
}

/**
 * Outbound references the row carries. The position/MTM routes are not wired yet, so these
 * are inert <span>s — they must render as neutral mono text, NOT blue-link-styled, so the
 * affordance is honest (WR-03). external_id is likewise plain mono. Blue is reserved for
 * elements that actually navigate (UI-SPEC Accent rule).
 */
function RowLinks({ event }: { event: ActivityEvent }) {
  const { external_id, mtm_id } = event.links;
  const items: React.ReactNode[] = [];

  if (external_id) {
    items.push(
      <span key="bo" className="font-mono text-gray-500">
        → BO txn {external_id}
      </span>,
    );
  }
  if (mtm_id !== null) {
    items.push(
      <span key="mtm" className="font-mono text-gray-500">
        → MTM run #{mtm_id}
      </span>,
    );
  }
  // LR-208 r5 L7: the "→ position" self-link rendered here always pointed at
  // THIS position (the Activity tab IS that position's tab) — a meaningless
  // self-reference rendered on every row. The placeholder for a future
  // cross-link to OTHER positions is reinstated when the route is wired up.

  return <span className="flex flex-wrap items-center gap-2 text-xs">{items}</span>;
}

/** A compact child row (dot omitted) inside the expanded batch region. */
function ChildRow({ child }: { child: ActivityEvent }) {
  const impact = formatSignedImpact(child.impact);
  // Custodian / CP side of the double entry — a backend-derived typed field (the
  // +/− sign already separates the two sides; the caption names which account
  // moved). Never parsed from `description`.
  const side = child.side;
  return (
    <div className="flex items-start justify-between gap-2 py-2">
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-900">
          {resolveLegLabel(child)}
          {side && <span className="font-normal text-lr-faint"> · {side}</span>}
        </p>
        <p className="text-xs text-lr-faint">
          {child.occurred_at.includes('T')
            ? formatTimeUtc(child.occurred_at)
            : formatDate(child.occurred_at)}
        </p>
      </div>
      <span
        className={clsx(
          'text-xs font-medium tabular-nums whitespace-nowrap',
          impact.tone === 'pos' && 'text-green-600',
          impact.tone === 'neg' && 'text-red-600',
          impact.tone === 'none' && 'text-gray-400',
        )}
      >
        {impact.text}
        {impact.caption && ` · ${impact.caption}`}
      </span>
    </div>
  );
}

/** Real two-leg (securities | collateral) layout — driven by the row's real change payload. */
function ChangePayload({ change }: { change: Record<string, unknown> }) {
  const entries = Object.entries(change);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1">
      {entries.map(([field, value]) => {
        // ADR-0001 §5: the change payload is keyed before→after (NOT old/new).
        const v = value as { before?: unknown; after?: unknown } | unknown;
        const hasBeforeAfter =
          v && typeof v === 'object' && ('before' in (v as object) || 'after' in (v as object));
        if (hasBeforeAfter) {
          const { before: beforeVal, after: afterVal } = v as { before?: unknown; after?: unknown };
          // Skip an all-empty field — it would render an uninformative
          // "field: — → —" (QA finding G).
          if (beforeVal == null && afterVal == null) return null;
          return (
            <p key={field} className="text-xs text-gray-500">
              {field}: <span className="text-gray-500">{String(beforeVal ?? '—')}</span>{' '}
              <span aria-hidden="true">→</span>{' '}
              <span className="text-gray-900">{String(afterVal ?? '—')}</span>
            </p>
          );
        }
        return (
          <p key={field} className="text-xs text-gray-500">
            {field}: <span className="text-gray-900">{String(v)}</span>
          </p>
        );
      })}
    </div>
  );
}

export function ActivityRow({ event, rowId, isExpanded, onToggle, trade }: ActivityRowProps) {
  // LR-208 r5 L8: fall back to the `lifecycle` family on an unrecognised value
  // so a malformed / future-typed row never crashes the tab with
  // "Cannot read property 'Icon' of undefined".
  const meta = FAMILY_META[event.family] ?? FAMILY_META.lifecycle;
  const Glyph = meta.Icon;
  const title = resolveEventLabel(event);
  const impact = formatSignedImpact(event.impact);
  const childCount = event.children.length;
  const detailRegionId = `activity-detail-${rowId}`;
  const titleIsRed = RED_TITLE_TYPES.has(event.event_type);
  // Settlement parent: the contractual value-date (open/close_settlement_date),
  // a typed field surfaced as metadata so the timeline can be ordered on the
  // real action time while the value-date stays visible (LR-417 §B).
  const valueDate = event.value_date;

  // before→after summary on the collapsed row, only when a real change exists.
  // ADR-0001 §5: change.price is keyed before→after (NOT old/new). Reading the
  // wrong keys rendered the mark price as "[object Object]" and suppressed the
  // collapsed summary entirely (F7: "MTM without price").
  const priceChange =
    event.change &&
    typeof event.change === 'object' &&
    'price' in event.change &&
    (event.change as { price?: { before?: unknown; after?: unknown } }).price
      ? (event.change as { price: { before?: unknown; after?: unknown } }).price
      : null;
  // Suppress an uninformative "Price — → —": show the summary only when a side
  // is actually present (QA finding G). before is null for a first/standalone
  // mark (no prior-value source — ADR Phase 1), so after alone is enough.
  const showPriceSummary =
    priceChange != null && (priceChange.before != null || priceChange.after != null);

  return (
    <div className="relative flex gap-4">
      {/* Dot + glyph */}
      <div
        className={clsx(
          'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-white z-10',
          resolveDotColor(event),
        )}
      >
        <Glyph className="h-3 w-3 text-white" aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={detailRegionId}
          className="group flex w-full items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={clsx(
                  'text-sm font-medium',
                  titleIsRed ? 'text-red-600' : 'text-gray-900',
                )}
              >
                {title}
              </span>
              <span
                className={clsx(
                  'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium',
                  meta.pill,
                )}
              >
                {meta.label}
              </span>
              {valueDate && (
                <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                  value date · {formatDate(valueDate)}
                </span>
              )}
              <ActorPill event={event} />
            </div>

            {showPriceSummary && priceChange && (
              <p className="mt-0.5 text-sm text-gray-600">
                Price <span className="text-gray-500">{String(priceChange.before ?? '—')}</span>{' '}
                <span aria-hidden="true">→</span>{' '}
                <span className="text-gray-900">{String(priceChange.after ?? '—')}</span>
              </p>
            )}

            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <span
                className={clsx(
                  'text-sm font-medium tabular-nums',
                  impact.tone === 'pos' && 'text-green-600',
                  impact.tone === 'neg' && 'text-red-600',
                  impact.tone === 'none' && 'text-gray-400',
                )}
              >
                {impact.text}
              </span>
              {impact.caption && (
                <span className="text-xs text-gray-500">· {impact.caption}</span>
              )}
              <RowLinks event={event} />
            </div>
          </div>

          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            <span className="lr-num text-xs text-lr-faint whitespace-nowrap">
              {/* Time-vs-date is chosen on the anchor's GRANULARITY, not the
                  `approximate` flag (LR-208 r6 M14): a date-only anchor
                  (hold/transfer/release transaction_date) is precise-to-the-day
                  but NOT a real instant, so rendering formatTimeUtc fabricated a
                  "00:00 UTC". Date-only → the date; datetime → UTC time. */}
              {event.occurred_at.includes('T')
                ? formatTimeUtc(event.occurred_at)
                : formatDate(event.occurred_at)}
            </span>
            {event.approximate && (
              <span
                title="Timestamp reconstructed from notes — exact time not recorded."
                className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700"
              >
                ≈ approximate · date-only
              </span>
            )}
            <span className="flex items-center gap-1 text-xs text-gray-400">
              {/* Expanding still reveals the BO context line + refs, so keep the
                  affordance — but drop the bare "0 details" count (QA finding G). */}
              {childCount > 0
                ? `▸ ${childCount} ${childCount === 1 ? 'detail' : 'details'}`
                : '▸ Details'}
              {isExpanded ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
              )}
            </span>
          </div>
        </button>

        {/* Expanded region */}
        {isExpanded && (
          <div id={detailRegionId} className="ml-9 mt-2 rounded-lg bg-gray-50 p-4">
            {event.change && <ChangePayload change={event.change} />}

            {/* LR-484: operator note captured at amend approval (null for other events). */}
            {event.note && (
              <p className="mt-2 text-xs italic text-gray-600">
                <span className="font-medium not-italic text-gray-500">Note:</span> {event.note}
              </p>
            )}

            <p className="mt-2 text-xs text-gray-500">
              {trade.symbol_id} · {trade.trade_type_display} · {trade.counterparty_name} ·{' '}
              {trade.agreement_display} · {trade.quantity.toLocaleString()}
            </p>

            {childCount > 0 && (
              <div className="mt-2 divide-y divide-gray-100">
                {event.children.map((child, i) => (
                  <ChildRow key={`${rowId}-child-${i}`} child={child} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
